import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { CloseButton, IconCalendarUi, IconPhone, IconVideo, Overlay, crmToast } from './crmUi'
import {
  createBooking, getBookingAgenda, getBookingPrefill, getDemosDay, getGcalDay, getUsers, updateBooking,
  type Booking, type BookingPrefill, type DayDemo, type GcalEvent,
} from './crmApi'
import { timeInTz, tzAbbr, tzDiffers, viewerTz } from './localTime'

/**
 * Book a call / meeting on a lead — Pipedrive functionality, our UI (PRD-04).
 *
 * Left: type toggle (Call | Meeting), title, date + time range, assignee, the lead's
 * phone (call, tap-to-dial) or attendee emails (meeting, prefilled with the lead's
 * contact), note, Save / Save & mark done.
 * Right: the assignee's agenda for the chosen day so you can see what's already booked.
 * Google Meet links are generated later by the Meet integration — meetings save now
 * and show "Meet link pending" until then.
 */

const pad = (n: number) => String(n).padStart(2, '0')

/** Country dial codes for the call feature. Default comes from the lead's country
 * (WeQuote API field, via booking-prefill). Ordered: our markets first, then A–Z. */
export const DIAL_CODES: Array<{ cc: string; name: string; dial: string; flag: string }> = [
  { cc: 'GB', name: 'United Kingdom', dial: '44', flag: '🇬🇧' },
  { cc: 'IE', name: 'Ireland', dial: '353', flag: '🇮🇪' },
  { cc: 'US', name: 'United States', dial: '1', flag: '🇺🇸' },
  { cc: 'AU', name: 'Australia', dial: '61', flag: '🇦🇺' },
  { cc: 'AE', name: 'UAE', dial: '971', flag: '🇦🇪' },
  { cc: 'AT', name: 'Austria', dial: '43', flag: '🇦🇹' },
  { cc: 'BE', name: 'Belgium', dial: '32', flag: '🇧🇪' },
  { cc: 'CA', name: 'Canada', dial: '1', flag: '🇨🇦' },
  { cc: 'CH', name: 'Switzerland', dial: '41', flag: '🇨🇭' },
  { cc: 'DE', name: 'Germany', dial: '49', flag: '🇩🇪' },
  { cc: 'DK', name: 'Denmark', dial: '45', flag: '🇩🇰' },
  { cc: 'ES', name: 'Spain', dial: '34', flag: '🇪🇸' },
  { cc: 'FI', name: 'Finland', dial: '358', flag: '🇫🇮' },
  { cc: 'FR', name: 'France', dial: '33', flag: '🇫🇷' },
  { cc: 'GR', name: 'Greece', dial: '30', flag: '🇬🇷' },
  { cc: 'HK', name: 'Hong Kong', dial: '852', flag: '🇭🇰' },
  { cc: 'IN', name: 'India', dial: '91', flag: '🇮🇳' },
  { cc: 'IT', name: 'Italy', dial: '39', flag: '🇮🇹' },
  { cc: 'NL', name: 'Netherlands', dial: '31', flag: '🇳🇱' },
  { cc: 'NO', name: 'Norway', dial: '47', flag: '🇳🇴' },
  { cc: 'NZ', name: 'New Zealand', dial: '64', flag: '🇳🇿' },
  { cc: 'PL', name: 'Poland', dial: '48', flag: '🇵🇱' },
  { cc: 'PT', name: 'Portugal', dial: '351', flag: '🇵🇹' },
  { cc: 'SA', name: 'Saudi Arabia', dial: '966', flag: '🇸🇦' },
  { cc: 'SE', name: 'Sweden', dial: '46', flag: '🇸🇪' },
  { cc: 'SG', name: 'Singapore', dial: '65', flag: '🇸🇬' },
  { cc: 'ZA', name: 'South Africa', dial: '27', flag: '🇿🇦' },
]
const dialFor = (cc: string | null) => DIAL_CODES.find((d) => d.cc === String(cc || '').toUpperCase())?.dial || '44'

/** Build E.164 from a country + national number: strip spaces/()-, drop the leading 0,
 * pass through numbers already entered with +. */
export const toE164 = (cc: string, national: string): string => {
  const raw = national.trim().replace(/[\s()-]/g, '')
  if (!raw) return ''
  if (raw.startsWith('+')) return raw
  if (raw.startsWith('00')) return `+${raw.slice(2)}`
  return `+${dialFor(cc)}${raw.replace(/^0+/, '')}`
}
const toDayIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const fmtTime = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '')

/** Round now up to the next half hour for a sensible default start. */
const defaultStart = () => {
  const d = new Date()
  d.setSeconds(0, 0)
  d.setMinutes(d.getMinutes() + 30 - (d.getMinutes() % 30))
  return d
}

/* ── Day timeline — an actual calendar column, like Google/Pipedrive ─────────
 * Hour grid with events absolutely positioned by time: Google Calendar events
 * (grey, dashed), CRM bookings (white, brand border), and the in-progress new
 * booking (solid blue, follows the time fields live). A red line marks "now" on
 * today. CLICKING an empty slot sets the booking's start time (snapped to 30
 * min, duration preserved). ‹ › steps days.
 */
const HOUR_H = 44 // px per hour
const timeToMin = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + (m || 0) }
const minToTime = (min: number) => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`
const dateToMin = (iso: string) => { const d = new Date(iso); return d.getHours() * 60 + d.getMinutes() }

const DayTimeline: React.FC<{
  day: string
  dayLabel: string
  assigneeName: string
  agenda: Booking[] | null
  gcal: { available: boolean; events: GcalEvent[] } | null
  demos: DayDemo[] | null
  /** Logged-in user's email — colleague demos render amber "info only" for them. */
  viewerEmail?: string | null
  startTime: string
  endTime: string
  newTitle: string
  newIcon: React.ReactNode
  onShiftDay: (delta: number) => void
  onPickTime: (start: string, end: string) => void
}> = ({ day, dayLabel, assigneeName, agenda, gcal, demos, viewerEmail, startTime, endTime, newTitle, newIcon, onShiftDay, onPickTime }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const loading = agenda === null || gcal === null || demos === null
  const dayDemoList = (demos || []).filter((d) => d.startAt)

  // De-dupe by identity only: hide Google events that ARE a CRM booking's mirror or a
  // Calendly demo's calendar copy (matching googleEventId / Meet link) — never by time.
  const mirrorIds = new Set([
    ...(agenda || []).flatMap((b) => [b.googleEventId, b.meetUrl].filter(Boolean) as string[]),
    ...dayDemoList.map((d) => d.meetUrl).filter(Boolean) as string[],
  ])
  const gEvents = (gcal?.events || []).filter(
    (e) => !e.allDay && toDayIso(new Date(e.startAt)) === day && !(e.gid && mirrorIds.has(e.gid)) && !(e.meetUrl && mirrorIds.has(e.meetUrl)),
  )
  const allDay = (gcal?.events || []).filter((e) => e.allDay)
  const crm = (agenda || []).filter((b) => b.startAt)

  const newStartMin = timeToMin(startTime)
  const newEndMin = endTime && timeToMin(endTime) > newStartMin ? timeToMin(endTime) : newStartMin + 30
  const durMin = newEndMin - newStartMin

  // Grid range: 07:00–21:00, stretched to fit anything scheduled outside it.
  const allStarts = [...gEvents.map((e) => dateToMin(e.startAt)), ...crm.map((b) => dateToMin(b.startAt!)), ...dayDemoList.map((d) => dateToMin(d.startAt!)), newStartMin]
  const allEnds = [
    ...gEvents.map((e) => (e.endAt ? dateToMin(e.endAt) : dateToMin(e.startAt) + 30)),
    ...crm.map((b) => (b.endAt ? dateToMin(b.endAt) : dateToMin(b.startAt!) + 30)),
    ...dayDemoList.map((d) => (d.endAt ? dateToMin(d.endAt) : dateToMin(d.startAt!) + 30)),
    newEndMin,
  ]
  const startH = Math.min(7, Math.floor(Math.min(...allStarts) / 60))
  const endH = Math.max(21, Math.ceil(Math.max(...allEnds) / 60))
  const gridMin = startH * 60
  const gridH = (endH - startH) * HOUR_H
  const topOf = (min: number) => ((min - gridMin) / 60) * HOUR_H
  const heightOf = (fromMin: number, toMin: number) => Math.max(18, ((toMin - fromMin) / 60) * HOUR_H)

  const isToday = toDayIso(new Date()) === day
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes()

  // Open scrolled to just above the pending booking.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: Math.max(0, topOf(newStartMin) - 80) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, agenda === null])

  const clickGrid = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const min = gridMin + ((e.clientY - rect.top) / HOUR_H) * 60
    const snapped = Math.max(0, Math.min(24 * 60 - durMin, Math.round(min / 30) * 30))
    onPickTime(minToTime(snapped), minToTime(snapped + durMin))
  }

  const eventBlock = (fromMin: number, toMin: number, cls: string, style: React.CSSProperties, line1: React.ReactNode, line2?: React.ReactNode, key?: string) => (
    <div
      key={key}
      className={`absolute left-11 right-1 rounded-md px-1.5 py-0.5 overflow-hidden pointer-events-none ${cls}`}
      style={{ top: topOf(fromMin), height: heightOf(fromMin, toMin), ...style }}
    >
      <p className="text-[10px] font-semibold truncate leading-tight">{line1}</p>
      {line2 && heightOf(fromMin, toMin) > 26 && <p className="text-[9px] opacity-80 truncate leading-tight">{line2}</p>}
    </div>
  )

  return (
    <div className="w-80 shrink-0 border-l border-border bg-surface-secondary flex flex-col min-h-0">
      {/* Header */}
      <div className="px-3 pt-3 pb-2 shrink-0">
        <div className="flex items-center gap-1.5">
          <button onClick={() => onShiftDay(-1)} className="w-6 h-6 rounded-md text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover" title="Previous day">‹</button>
          <p className="text-[12px] font-bold text-txt-primary flex-1 text-center">{dayLabel}</p>
          <button onClick={() => onShiftDay(1)} className="w-6 h-6 rounded-md text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover" title="Next day">›</button>
        </div>
        <p className="text-[11px] text-txt-tertiary text-center">{assigneeName}’s day · click the grid to set the time</p>
        {gcal && !gcal.available && (
          <p className="text-[10px] text-txt-tertiary mt-1 rounded-md bg-surface px-2 py-1 border border-border text-center">
            Google Calendar unavailable — CRM bookings only.
          </p>
        )}
        {allDay.length > 0 && (
          <div className="mt-1.5 space-y-1">
            {allDay.map((e, i) => (
              <div key={i} className="rounded-md bg-surface border border-dashed border-border px-2 py-1">
                <p className="text-[10px] font-semibold text-txt-secondary truncate"><IconCalendarUi className="w-3 h-3 inline -mt-0.5" /> {e.title} · all day</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* The grid */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <p className="text-[11px] text-txt-tertiary px-3 py-4">Loading calendar…</p>
        ) : (
          <div className="relative mx-2 mb-3 cursor-pointer select-none" style={{ height: gridH }} onClick={clickGrid} title="Click to set the booking time">
            {/* Hour lines + labels */}
            {Array.from({ length: endH - startH + 1 }, (_, i) => (
              <div key={i} className="absolute left-0 right-0 flex items-start pointer-events-none" style={{ top: i * HOUR_H }}>
                <span className="w-10 pr-1.5 text-right text-[9px] text-txt-tertiary -translate-y-1/2">{pad(startH + i)}:00</span>
                <div className="flex-1 border-t border-border/70" />
              </div>
            ))}
            {/* Half-hour lines */}
            {Array.from({ length: endH - startH }, (_, i) => (
              <div key={`h${i}`} className="absolute left-11 right-0 border-t border-border/30 pointer-events-none" style={{ top: i * HOUR_H + HOUR_H / 2 }} />
            ))}

            {/* Google events */}
            {gEvents.map((e, i) =>
              eventBlock(
                dateToMin(e.startAt),
                e.endAt ? dateToMin(e.endAt) : dateToMin(e.startAt) + 30,
                '',
                { background: '#F8FAFC', border: '1.5px dashed #CBD5E1', color: '#64748B' },
                <><IconCalendarUi className="w-3 h-3 inline -mt-0.5" /> {e.title}</>,
                `${fmtTime(e.startAt)}${e.endAt ? `–${fmtTime(e.endAt)}` : ''} · Google`,
                `g${i}`,
              ),
            )}

            {/* Calendly demos — ALL hosts. A colleague's demo renders AMBER ("info
                only"); the host themselves sees normal demo purple. Viewer-relative:
                the same demo looks different to Andy vs Natalie, by design. */}
            {dayDemoList.map((d) => {
              // Server-computed `mine` includes the demo-attendee group (Andy, Lee,
              // WeQuote Support, Jennie); fall back to host comparison for safety.
              const mine = d.mine ?? (!!d.hostEmail && !!viewerEmail && d.hostEmail === viewerEmail)
              // Yours = blue; not yours = grey/light-black "info only" (Lee, 3 Aug rev 2).
              const style = mine
                ? { background: '#EFF6FF', border: '1.5px dashed #3B82F6', color: '#1E3A8A' }
                : { background: '#F3F4F6', border: '1.5px dashed #4B5563', color: '#374151' }
              return eventBlock(
                dateToMin(d.startAt!),
                d.endAt ? dateToMin(d.endAt) : dateToMin(d.startAt!) + 30,
                '',
                style,
                <><IconVideo className="w-3 h-3 inline -mt-0.5" /> Demo — {d.dealName || d.inviteeName || d.title}</>,
                `${fmtTime(d.startAt)}${d.endAt ? `–${fmtTime(d.endAt)}` : ''}${mine ? ' · your demo' : d.hostEmail ? ` · ${d.hostEmail} — info only` : ' · host unassigned'}`,
                `dm${d.id}`,
              )
            })}

            {/* CRM bookings — the diary owner's own: blue soft-tint style. */}
            {crm.map((b) =>
              eventBlock(
                dateToMin(b.startAt!),
                b.endAt ? dateToMin(b.endAt) : dateToMin(b.startAt!) + 30,
                '',
                { background: '#EFF6FF', border: '1.5px dashed #3B82F6', color: '#1E3A8A' },
                <>{b.type === 'call' ? <IconPhone className="w-3 h-3 inline -mt-0.5" /> : <IconCalendarUi className="w-3 h-3 inline -mt-0.5" />} {b.title}</>,
                `${fmtTime(b.startAt)}${b.endAt ? `–${fmtTime(b.endAt)}` : ''}${b.dealName ? ` · ${b.dealName}` : ''}`,
                b.id,
              ),
            )}

            {/* The in-progress NEW booking — solid blue, follows the form live */}
            {eventBlock(
              newStartMin,
              newEndMin,
              'text-white z-10',
              { background: '#2563EB', boxShadow: '0 1px 4px rgba(37,99,235,.4)' },
              <>{newIcon} {newTitle}</>,
              `${minToTime(newStartMin)}–${minToTime(newEndMin)} · new booking`,
              'new',
            )}

            {/* Now line */}
            {isToday && nowMin >= gridMin && nowMin <= endH * 60 && (
              <div className="absolute left-0 right-0 pointer-events-none z-20" style={{ top: topOf(nowMin) }}>
                <div className="flex items-center">
                  <span className="w-10 pr-1 text-right text-[9px] font-bold text-red-600 -translate-y-1/2">{minToTime(nowMin)}</span>
                  <div className="flex-1 border-t-2 border-red-500" />
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 -ml-0.5 -translate-y-1/2" />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export const BookingModal: React.FC<{
  dealId: string
  dealName?: string
  initialType?: 'call' | 'meeting'
  /** EDIT MODE: change an existing booking — Google gets patched and re-notifies. */
  editBooking?: Booking
  /** Prefill for "book a follow-up": title/attendees/note seeded from the original. */
  initial?: { title?: string; attendees?: string[]; note?: string }
  onClose: () => void
  onSaved?: (b: Booking) => void
}> = ({ dealId, dealName, initialType = 'call', editBooking, initial, onClose, onSaved }) => {
  const { currentAgent } = useAuth()
  const eb = editBooking
  const ebStart = eb?.startAt ? new Date(eb.startAt) : null
  const ebEnd = eb?.endAt ? new Date(eb.endAt) : null
  const [type, setType] = useState<'call' | 'meeting'>(eb?.type || initialType)
  const [title, setTitle] = useState(eb?.title || initial?.title || '')
  const [day, setDay] = useState(ebStart ? toDayIso(ebStart) : toDayIso(defaultStart()))
  const [startTime, setStartTime] = useState(() => {
    if (ebStart) return `${pad(ebStart.getHours())}:${pad(ebStart.getMinutes())}`
    const d = defaultStart(); return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  })
  const [endTime, setEndTime] = useState(() => {
    if (ebEnd) return `${pad(ebEnd.getHours())}:${pad(ebEnd.getMinutes())}`
    const d = defaultStart(); d.setMinutes(d.getMinutes() + 30); return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  })
  const [assigneeId, setAssigneeId] = useState<string>(eb?.assigneeId || String(currentAgent?.id || ''))
  const [users, setUsers] = useState<any[]>([])
  const [prefill, setPrefill] = useState<BookingPrefill | null>(null)
  const [phone, setPhone] = useState(eb?.phone || '')
  const [phoneCc, setPhoneCc] = useState('GB')
  const [attendees, setAttendees] = useState<string[]>(eb?.attendees || initial?.attendees || [])
  const [attendeeInput, setAttendeeInput] = useState('')
  const [suggestOpen, setSuggestOpen] = useState(false)
  const [note, setNote] = useState(eb?.note || initial?.note || '')
  const [agenda, setAgenda] = useState<Booking[] | null>(null)
  const [gcal, setGcal] = useState<{ available: boolean; events: GcalEvent[] } | null>(null)
  const [dayDemos, setDayDemos] = useState<DayDemo[] | null>(null)
  const [saving, setSaving] = useState(false)
  // Qualification gate (Fi's PRD, 18 Sep 2026): the server 409s a demo booking
  // on a lead under the threshold until a one-line reason is logged.
  const [qualPrompt, setQualPrompt] = useState<string | null>(null)
  const [qualReason, setQualReason] = useState('')
  // The reassuring confirmation screen shown after a successful booking (Lee, 31 Jul 2026).
  const [confirmed, setConfirmed] = useState<Booking | null>(null)
  const [copied, setCopied] = useState(false)
  // Lazy-loaded DealDetail (dynamic import avoids the BookingModal↔DealDetail cycle).
  const [LeadComp, setLeadComp] = useState<React.ComponentType<any> | null>(null)
  const [leadData, setLeadData] = useState<any | null>(null)
  const [leadLoading, setLeadLoading] = useState(false)
  const openLead = () => {
    setLeadLoading(true)
    Promise.all([import('./crmApi').then((m) => m.getDealById(dealId)), import('./DealDetail')])
      .then(([d, m]) => { setLeadComp(() => m.DealDetail); setLeadData(d) })
      .catch(() => crmToast('Could not open the lead', 'error'))
      .finally(() => setLeadLoading(false))
  }

  useEffect(() => {
    getUsers().then(setUsers).catch(() => {})
    getBookingPrefill(dealId).then((p) => {
      setPrefill(p)
      setPhone((cur) => cur || p.phone || '')
      // Default dial code from the lead's country (WeQuote API field).
      if (p.country && DIAL_CODES.some((d) => d.cc === p.country!.toUpperCase())) setPhoneCc(p.country.toUpperCase())
      setAttendees((cur) => (cur.length ? cur : p.email ? [p.email] : []))
    }).catch(() => {})
  }, [dealId])

  // Day agenda for the chosen assignee + date: our own bookings AND their real
  // Google Calendar (impersonated read via the notes-pipeline service account).
  const assigneeEmail = users.find((u) => String(u.id) === assigneeId)?.email || (String(currentAgent?.id || '') === assigneeId ? currentAgent?.email : null)
  useEffect(() => {
    if (!assigneeId) { setAgenda([]); return }
    let cancelled = false
    setGcal(null)
    setDayDemos(null)
    getBookingAgenda(assigneeId, day).then((a) => { if (!cancelled) setAgenda(a) }).catch(() => { if (!cancelled) setAgenda([]) })
    // ALL Calendly demos that day (any host) — nobody books over an existing demo.
    getDemosDay(day).then((d) => { if (!cancelled) setDayDemos(d) }).catch(() => { if (!cancelled) setDayDemos([]) })
    if (assigneeEmail) {
      getGcalDay(assigneeEmail, day).then((g) => { if (!cancelled) setGcal(g) }).catch(() => { if (!cancelled) setGcal({ available: false, events: [] }) })
    } else {
      setGcal({ available: false, events: [] })
    }
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigneeId, day, assigneeEmail])

  const shiftDay = (delta: number) => {
    const d = new Date(`${day}T12:00:00`)
    d.setDate(d.getDate() + delta)
    setDay(toDayIso(d))
  }

  // Build an ISO string only from a COMPLETE, valid HH:MM. While editing a time
  // field it can momentarily be '' or partial → new Date(...) is Invalid and
  // .toISOString() THROWS (RangeError), which crashed the whole modal to a white
  // screen. Return null instead and let save() handle a missing time.
  const toIso = (d: string, time: string): string | null => {
    if (!time || !/^\d{2}:\d{2}$/.test(time)) return null
    const dt = new Date(`${d}T${time}:00`)
    return Number.isNaN(dt.getTime()) ? null : dt.toISOString()
  }
  const startIso = useMemo(() => toIso(day, startTime), [day, startTime])
  const endIso = useMemo(() => toIso(day, endTime), [day, endTime])
  const timeInvalid = !!endTime && endTime <= startTime

  const addAttendee = (raw?: string) => {
    const e = (raw ?? attendeeInput).trim().toLowerCase()
    if (!e) return
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { crmToast('Not a valid email address', 'error'); return }
    if (!attendees.includes(e)) setAttendees((cur) => (cur.includes(e) ? cur : [...cur, e]))
    setAttendeeInput('')
  }

  // Type-ahead pool (Lee, 31 Jul 2026): THIS lead's contacts + WeTask staff, filtered
  // as you type by name or email; free-text emails still allowed. Grows to every org
  // user once Robert ships the WeQuote users feed — same list, more entries.
  const attendeeSuggestions = useMemo(() => {
    const pool: { name: string | null; email: string; kind: 'lead' | 'staff' }[] = [
      ...(prefill?.contacts || []).map((c) => ({ ...c, kind: 'lead' as const })),
      ...users.filter((u) => u.email).map((u) => ({ name: u.name || null, email: String(u.email).toLowerCase(), kind: 'staff' as const })),
    ].filter((s) => !attendees.includes(s.email))
    const needle = attendeeInput.trim().toLowerCase()
    const filtered = needle
      ? pool.filter((s) => s.email.includes(needle) || (s.name || '').toLowerCase().includes(needle))
      : pool
    return filtered.slice(0, 8)
  }, [prefill, users, attendees, attendeeInput])

  const save = async (markDone: boolean) => {
    if (!startIso) { crmToast('Set a start time', 'error'); return }
    if (timeInvalid) { crmToast('End time must be after the start time', 'error'); return }
    // Calls logged as already-held need the caller's remarks (forced logging rule).
    if (markDone && type === 'call' && !note.trim()) {
      crmToast('Add notes about the call first — logging is required', 'error')
      return
    }
    setSaving(true)
    try {
      if (eb) {
        // EDIT: patch the booking — the server patches the Google event too and
        // Google re-notifies every attendee of the change.
        const b = await updateBooking(eb.id, {
          title: title.trim() || undefined,
          startAt: startIso,
          endAt: endIso,
          phone: type === 'call' ? (toE164(phoneCc, phone) || null) : undefined,
          attendees: type === 'meeting' ? attendees : undefined,
          note: note.trim() || null,
        })
        crmToast(type === 'meeting' ? 'Booking updated — Google has re-notified the attendees' : 'Booking updated', 'success')
        onSaved?.(b)
        onClose()
        return
      }
      const b = await createBooking(dealId, {
        type,
        title: title.trim() || undefined,
        startAt: startIso,
        endAt: endIso,
        assigneeId: assigneeId || null,
        // Stored in E.164 (+44…) so every tel: link — web, schedule feed, mobile — dials
        // correctly regardless of where the caller is.
        phone: type === 'call' ? (toE164(phoneCc, phone) || null) : null,
        attendees: type === 'meeting' ? attendees : undefined,
        note: note.trim() || null,
        qualReason: qualReason.trim() || undefined,
      })
      if (markDone) await updateBooking(b.id, { status: 'done', logText: note.trim() || null })
      onSaved?.(b)
      if (markDone) {
        crmToast('Logged and completed', 'success')
        onClose()
      } else {
        // Show the confirmation screen instead of silently closing.
        setConfirmed(b)
      }
    } catch (e: any) {
      if (e?.status === 409) {
        // Unqualified-demo gate: show the server's banner + the reason box.
        setQualPrompt(e.message || 'This lead is under the qualification threshold — fill the gates or log a reason to proceed.')
      } else {
        crmToast('Could not save the booking', 'error')
      }
    } finally {
      setSaving(false)
    }
  }

  const inputCls = 'rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-txt-primary focus:outline-none focus:border-brand-400'
  const dayLabel = new Date(`${day}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })

  // ── Confirmation screen — everything that just got locked in, in plain words. ──
  if (confirmed) {
    const hostName = users.find((u) => String(u.id) === String(confirmed.assigneeId))?.name || 'you'
    const when = confirmed.startAt
      ? `${new Date(confirmed.startAt).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} · ${fmtTime(confirmed.startAt)}${confirmed.endAt ? `–${fmtTime(confirmed.endAt)}` : ''}`
      : ''
    const isMeeting = confirmed.type === 'meeting'
    return (
      <Overlay onClose={() => { onClose() }} width={560}>
        <div className="p-7 text-center">
          <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ background: '#DCFCE7' }}>
            <svg className="w-9 h-9" fill="none" viewBox="0 0 24 24" stroke="#16A34A" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-txt-primary mt-4">{isMeeting ? 'Meeting booked' : 'Call booked'} ✓</h2>
          <p className="text-sm text-txt-secondary mt-1">
            <span className="font-semibold text-txt-primary">{confirmed.title}</span>
            {(dealName || prefill?.dealName) && <> with <span className="font-semibold text-txt-primary">{dealName || prefill?.dealName}</span></>}
          </p>

          <div className="mt-5 rounded-2xl border border-border bg-surface-secondary text-left divide-y divide-border">
            <div className="px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">When</p>
              <p className="text-[14px] font-semibold text-txt-primary mt-0.5">{when}</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">{isMeeting ? 'Host' : 'Assigned to'}</p>
              <p className="text-[14px] font-semibold text-txt-primary mt-0.5">{hostName}</p>
            </div>
            {isMeeting ? (
              <div className="px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Invited</p>
                {confirmed.attendees.length ? (
                  <>
                    <div className="flex items-center gap-1.5 flex-wrap mt-1">
                      {confirmed.attendees.map((a) => (
                        <span key={a} className="px-2 py-1 rounded-full text-[11px] font-medium bg-surface border border-border text-txt-secondary">{a}</span>
                      ))}
                    </div>
                    <p className="text-[11px] text-txt-tertiary mt-1.5">
                      The event is on {hostName === 'you' ? 'your' : `${hostName}’s`} Google Calendar and every attendee has been emailed their invite by Google — nothing else to send.
                    </p>
                  </>
                ) : (
                  <p className="text-[13px] text-txt-secondary mt-0.5">No attendees — the event is on the calendar for {hostName === 'you' ? 'you' : hostName} only.</p>
                )}
              </div>
            ) : (
              confirmed.phone && (
                <div className="px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Number to dial</p>
                  <p className="text-[14px] font-semibold text-txt-primary mt-0.5">{confirmed.phone}</p>
                  <p className="text-[11px] text-txt-tertiary mt-1">It is in your Calls tab for that day — dial from there and the call log will be waiting.</p>
                </div>
              )
            )}
            {isMeeting && (
              <div className="px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Google Meet</p>
                {confirmed.meetUrl ? (
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <a href={confirmed.meetUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg text-xs font-bold text-white" style={{ background: '#2F6FB0' }}>Join Meet</a>
                    <button
                      onClick={() => { navigator.clipboard?.writeText(confirmed.meetUrl!).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) }) }}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-primary hover:bg-surface-hover"
                    >
                      {copied ? '✓ Copied' : 'Copy link'}
                    </button>
                    <span className="text-[11px] text-txt-tertiary truncate max-w-[200px]">{confirmed.meetUrl}</span>
                  </div>
                ) : (
                  <p className="text-[12px] text-amber-700 mt-0.5">The Calendar event could not be created automatically — the booking is saved in the CRM; check the lead’s activity feed.</p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 mt-6">
            <button onClick={openLead} className="px-4 py-2 rounded-lg text-sm font-bold text-brand-600 border border-brand-300 hover:bg-brand-50">
              {leadLoading ? 'Opening…' : 'Open lead →'}
            </button>
            <button onClick={onClose} className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-brand-600 hover:bg-brand-700">Done</button>
          </div>
        </div>
        {LeadComp && leadData && <LeadComp deal={leadData} onClose={() => setLeadData(null)} />}
      </Overlay>
    )
  }

  return (
    <Overlay onClose={onClose} width={860}>
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
        <h2 className="text-base font-bold text-txt-primary">
          {eb ? `Edit ${type === 'call' ? 'call' : 'meeting'}` : `Book ${type === 'call' ? 'a call' : 'a meeting'}`}
          {(dealName || prefill?.dealName) && <span className="text-txt-secondary font-medium"> — {dealName || prefill?.dealName}</span>}
        </h2>
        <CloseButton onClick={onClose} />
      </div>

      <div className="flex min-h-0" style={{ maxHeight: '72vh' }}>
        {/* ── Left: the form ── */}
        <div className="flex-1 min-w-0 overflow-y-auto p-5 space-y-4">
          {/* Type toggle — locked in edit mode (a meeting cannot become a call: the
              Google event is type-bound; cancel and rebook instead). */}
          <div className="inline-flex items-center p-0.5 rounded-lg bg-surface-tertiary border border-border">
            {(['call', 'meeting'] as const).map((t) => (
              <button
                key={t}
                onClick={() => !eb && setType(t)}
                disabled={!!eb && type !== t}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                  type === t ? 'bg-surface text-txt-primary shadow-sm' : 'text-txt-secondary hover:text-txt-primary'
                } ${eb && type !== t ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                {t === 'call' ? <><IconPhone className="w-3.5 h-3.5" /> Call</> : <><IconCalendarUi className="w-3.5 h-3.5" /> Meeting</>}
              </button>
            ))}
          </div>

          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={type === 'call' ? 'Call the lead' : 'Meeting with the lead'}
            className={`${inputCls} w-full text-[15px] font-medium`}
          />

          {/* When */}
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} className={inputCls} />
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={inputCls} />
            <span className="text-txt-tertiary">–</span>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={`${inputCls} ${timeInvalid ? 'border-red-400' : ''}`} />
            {timeInvalid && <span className="text-[11px] text-error">ends before it starts</span>}
          </div>

          {/* WHAT TIME IS THAT FOR THEM (Lee, 23 Sep 2026): the chosen slot in
              your zone AND the lead's local zone (from their country), so a 4pm
              booking never lands at their 11pm by accident. */}
          {startIso && prefill?.tz && (() => {
            const leadTz = prefill.tz!
            const myTz = viewerTz()
            const s = new Date(startIso)
            const e = endIso ? new Date(endIso) : null
            const fmt = (tz: string) => `${timeInTz(tz, s)}${e ? `–${timeInTz(tz, e)}` : ''} ${tzAbbr(tz, s)}`
            const differs = tzDiffers(leadTz, myTz, s)
            return (
              <div className={`rounded-lg border px-3 py-2 text-[12px] flex items-center gap-4 flex-wrap ${differs ? 'border-amber-200 bg-amber-50' : 'border-border bg-surface-secondary'}`}>
                <span className="text-txt-secondary">Your time: <span className="font-bold text-txt-primary">{fmt(myTz)}</span></span>
                <span className={differs ? 'text-amber-800' : 'text-txt-secondary'}>
                  Their local time ({leadTz.split('/').pop()?.replace(/_/g, ' ')}): <span className="font-bold">{fmt(leadTz)}</span>
                </span>
                {!differs && <span className="text-[11px] text-txt-tertiary">same zone as you</span>}
              </div>
            )
          })()}

          {/* Assignee — locked in edit mode: the Google event lives on this host's
              calendar; moving host means cancel + rebook. */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-1">Assigned to</p>
            <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} disabled={!!eb} className={`${inputCls} w-full disabled:opacity-60 disabled:cursor-not-allowed`}>
              {users.map((u) => (
                <option key={u.id} value={String(u.id)}>{u.name}{String(u.id) === String(currentAgent?.id) ? ' (you)' : ''}</option>
              ))}
            </select>
            {eb && <p className="text-[10px] text-txt-tertiary mt-1">The calendar event lives on this host’s calendar — to change host, cancel and rebook.</p>}
          </div>

          {/* Call: the number to dial · Meeting: attendees */}
          {type === 'call' ? (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-1">Number to call</p>
              <div className="flex items-center gap-2">
                {/* Country dial code — defaults to the lead's country from the WeQuote API */}
                <select
                  value={phoneCc}
                  onChange={(e) => setPhoneCc(e.target.value)}
                  className={`${inputCls} w-[110px] shrink-0`}
                  title="Country dial code (defaults to the lead's country)"
                >
                  {DIAL_CODES.map((d) => (
                    <option key={d.cc} value={d.cc}>{d.flag} +{d.dial} {d.cc}</option>
                  ))}
                </select>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={prefill?.phone ? '' : 'No number on this lead — add one'}
                  className={`${inputCls} flex-1`}
                />
                {phone.trim() && (
                  <a href={`tel:${toE164(phoneCc, phone)}`} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700" title={`Dial ${toE164(phoneCc, phone)}`}>
                    Call now
                  </a>
                )}
              </div>
              {phone.trim() && (
                <p className="text-[10px] text-txt-tertiary mt-1">Dials as <span className="font-semibold text-txt-secondary">{toE164(phoneCc, phone)}</span></p>
              )}
              {prefill && prefill.phones.length > 1 && (
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  {prefill.phones.map((p) => (
                    <button key={p} onClick={() => setPhone(p)} className="px-2 py-0.5 rounded-full text-[11px] border border-border text-txt-secondary hover:bg-surface-hover">{p}</button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-1">Attendees (invited by email)</p>
              <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                {attendees.map((a) => (
                  <span key={a} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold bg-brand-50 text-brand-700 border border-brand-200">
                    {a}
                    <button onClick={() => setAttendees(attendees.filter((x) => x !== a))} className="hover:text-brand-900 font-bold">✕</button>
                  </span>
                ))}
                {attendees.length === 0 && <span className="text-[11px] text-txt-tertiary">No attendees yet</span>}
              </div>
              <div className="flex items-center gap-2 relative">
                <input
                  value={attendeeInput}
                  onChange={(e) => { setAttendeeInput(e.target.value); setSuggestOpen(true) }}
                  onFocus={() => setSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      // Enter picks the top suggestion when typing matched one; else adds free text.
                      if (attendeeInput.trim() && attendeeSuggestions.length && (attendeeSuggestions[0].email.includes(attendeeInput.trim().toLowerCase()) || (attendeeSuggestions[0].name || '').toLowerCase().includes(attendeeInput.trim().toLowerCase())) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(attendeeInput.trim())) {
                        addAttendee(attendeeSuggestions[0].email)
                      } else addAttendee()
                    }
                    if (e.key === 'Escape') setSuggestOpen(false)
                  }}
                  placeholder="Type a name or email — lead contacts & staff suggested"
                  className={`${inputCls} flex-1`}
                />
                <button onClick={() => addAttendee()} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-primary hover:bg-surface-hover">Add</button>
                {/* Type-ahead: this lead's contacts + WeTask staff. Free-text emails still work. */}
                {suggestOpen && attendeeSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-14 mt-1 z-30 rounded-xl bg-surface border border-border shadow-float overflow-hidden max-h-56 overflow-y-auto">
                    {attendeeSuggestions.map((s) => (
                      <button
                        key={s.email}
                        onMouseDown={(e) => { e.preventDefault(); addAttendee(s.email) }}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-surface-hover"
                      >
                        <span className="min-w-0">
                          <span className="block text-[12px] font-semibold text-txt-primary truncate">{s.name || s.email}</span>
                          {s.name && <span className="block text-[11px] text-txt-tertiary truncate">{s.email}</span>}
                        </span>
                        <span
                          className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase shrink-0"
                          style={s.kind === 'lead' ? { background: '#DBEAFE', color: '#1E40AF' } : { background: '#F1F5F9', color: '#334155' }}
                        >
                          {s.kind === 'lead' ? 'Lead contact' : 'WeTask staff'}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {prefill && prefill.emails.filter((e) => !attendees.includes(e)).length > 0 && (
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  {prefill.emails.filter((e) => !attendees.includes(e)).map((e) => (
                    <button key={e} onClick={() => setAttendees([...attendees, e])} className="px-2 py-0.5 rounded-full text-[11px] border border-border text-txt-secondary hover:bg-surface-hover">+ {e}</button>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-txt-tertiary mt-1.5">
                Booking creates the real Google Calendar event on the assignee’s calendar — Google emails the invites and generates the Meet link automatically.
              </p>
            </div>
          )}

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Notes…"
            rows={3}
            className={`${inputCls} w-full resize-none`}
          />

          {qualPrompt && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-2">
              <p className="text-[12px] font-semibold text-red-700">{qualPrompt}</p>
              <input
                autoFocus
                value={qualReason}
                onChange={(e) => setQualReason(e.target.value)}
                placeholder='Reason for proceeding, e.g. "referral, skipping standard vetting"'
                className={`${inputCls} w-full`}
              />
              <p className="text-[10px] text-red-600/80">The reason is logged on the lead and managers are alerted. Or close this, fill the qualification gates, and book clean.</p>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-3.5 py-2 rounded-lg text-sm font-medium text-txt-secondary hover:bg-surface-hover">Cancel</button>
            {!eb && (
              <button
                onClick={() => save(true)}
                disabled={saving}
                className="px-3.5 py-2 rounded-lg text-sm font-semibold border border-border text-txt-primary hover:bg-surface-hover disabled:opacity-50"
                title="Log it as already held"
              >
                Save & mark done
              </button>
            )}
            <button
              onClick={() => save(false)}
              disabled={saving || timeInvalid}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : eb ? 'Save changes' : type === 'call' ? 'Book call' : 'Book meeting'}
            </button>
          </div>
        </div>

        {/* ── Right: the assignee's day as a REAL calendar grid (Google + CRM + the
            in-progress booking). Click anywhere on the grid to set the time. ── */}
        <DayTimeline
          day={day}
          dayLabel={dayLabel}
          assigneeName={users.find((u) => String(u.id) === assigneeId)?.name || 'Assignee'}
          agenda={agenda}
          gcal={gcal}
          demos={dayDemos}
          viewerEmail={currentAgent?.email ? String(currentAgent.email).toLowerCase() : null}
          startTime={startTime}
          endTime={endTime}
          newTitle={title.trim() || (type === 'call' ? 'Call the lead' : 'Meeting with the lead')}
          newIcon={type === 'call' ? <IconPhone className="w-3.5 h-3.5 inline -mt-0.5" /> : <IconCalendarUi className="w-3.5 h-3.5 inline -mt-0.5" />}
          onShiftDay={shiftDay}
          onPickTime={(hhmm, endHhmm) => { setStartTime(hhmm); setEndTime(endHhmm) }}
        />
      </div>
    </Overlay>
  )
}

/** Small two-choice popover trigger: Book call | Book meeting. Renders the modal itself. */
export const QuickBookButton: React.FC<{
  dealId: string
  dealName?: string
  compact?: boolean
  onSaved?: () => void
}> = ({ dealId, dealName, compact = false, onSaved }) => {
  const [menu, setMenu] = useState(false)
  const [modalType, setModalType] = useState<'call' | 'meeting' | null>(null)

  return (
    <div className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setMenu((v) => !v)}
        title="Book a call or meeting"
        className={
          compact
            ? 'p-1 rounded-md text-txt-tertiary hover:text-brand-600 hover:bg-surface-hover transition-colors'
            : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-txt-primary hover:bg-surface-hover transition-colors'
        }
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5M12 12.75h.008v.008H12v-.008z" />
        </svg>
        {!compact && <span>Book</span>}
      </button>
      {menu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
          <div className="absolute right-0 top-full mt-1 z-50 w-44 rounded-lg bg-surface border border-border shadow-float overflow-hidden">
            <button
              onClick={() => { setMenu(false); setModalType('call') }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-txt-primary hover:bg-surface-hover"
            >
              <IconPhone className="w-3.5 h-3.5" /> Book a call
            </button>
            <button
              onClick={() => { setMenu(false); setModalType('meeting') }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-txt-primary hover:bg-surface-hover"
            >
              <IconCalendarUi className="w-3.5 h-3.5" /> Book a meeting
            </button>
          </div>
        </>
      )}
      {modalType && (
        <BookingModal
          dealId={dealId}
          dealName={dealName}
          initialType={modalType}
          onClose={() => setModalType(null)}
          onSaved={() => onSaved?.()}
        />
      )}
    </div>
  )
}
